import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Text,
  Paper,
  Stack,
  Group,
  Table,
  Button,
  Skeleton,
  SimpleGrid,
  Divider,
  Textarea,
  Select,
  Modal,
  Alert,
  NumberInput,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconDownload,
  IconCreditCard,
  IconAlertTriangle,
  IconMail,
  IconCalendar,
  IconDiscount2,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import PageHeader from '@/components/PageHeader';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import {
  useInvoice,
  useDownloadInvoicePdf,
  useSubmitDispute,
  useEmailInvoiceCopy,
  useDiscountEligibility,
  useCreatePaymentPlan,
} from '@/hooks/usePortalApi';
import dayjs from 'dayjs';

const DISPUTE_REASONS = [
  { value: 'incorrect_amount', label: 'Incorrect amount' },
  { value: 'wrong_items', label: 'Wrong items' },
  { value: 'duplicate', label: 'Duplicate invoice' },
  { value: 'not_received', label: 'Goods/services not received' },
  { value: 'other', label: 'Other' },
];

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const invoiceId = parseInt(id || '0', 10);
  const { data: invoice, isLoading } = useInvoice(invoiceId);
  const downloadPdf = useDownloadInvoicePdf();
  const submitDispute = useSubmitDispute();
  const emailCopy = useEmailInvoiceCopy();
  const { data: discount } = useDiscountEligibility(invoiceId);
  const createPlan = useCreatePaymentPlan();

  const [disputeOpen, { open: openDispute, close: closeDispute }] = useDisclosure(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeDetails, setDisputeDetails] = useState('');
  const [planOpen, { open: openPlan, close: closePlan }] = useDisclosure(false);
  const [planInstallments, setPlanInstallments] = useState(3);
  const [planFrequency, setPlanFrequency] = useState('monthly');

  const handleDownloadPdf = () => {
    downloadPdf.mutate(invoiceId, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice-${invoice?.invoice_number ?? invoiceId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      },
      onError: () => {
        notifications.show({
          title: 'Download failed',
          message: 'Unable to download PDF. Please try again.',
          color: 'red',
        });
      },
    });
  };

  const handleSubmitDispute = () => {
    if (!disputeReason) return;
    submitDispute.mutate(
      { invoice_id: invoiceId, reason: disputeReason, details: disputeDetails },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Dispute submitted',
            message: 'We will review your dispute and get back to you.',
            color: 'brand',
          });
          closeDispute();
          setDisputeReason('');
          setDisputeDetails('');
        },
        onError: () => {
          notifications.show({
            title: 'Submission failed',
            message: 'Unable to submit dispute. Please try again.',
            color: 'red',
          });
        },
      },
    );
  };

  const handleEmailCopy = () => {
    emailCopy.mutate(invoiceId, {
      onSuccess: () => {
        notifications.show({ title: 'Email sent', message: 'Invoice copy emailed to you.', color: 'green' });
      },
      onError: () => {
        notifications.show({ title: 'Email failed', message: 'Unable to send email.', color: 'red' });
      },
    });
  };

  const handleCreatePlan = () => {
    if (!invoice) return;
    createPlan.mutate(
      {
        invoice_id: invoiceId,
        total_amount: invoice.balance_due,
        num_installments: planInstallments,
        frequency: planFrequency,
      },
      {
        onSuccess: () => {
          notifications.show({ title: 'Plan created', message: 'Payment plan set up successfully.', color: 'green' });
          closePlan();
        },
        onError: () => {
          notifications.show({ title: 'Failed', message: 'Unable to create payment plan.', color: 'red' });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={40} width={300} />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height={80} />
          ))}
        </SimpleGrid>
        <Skeleton height={200} />
      </Stack>
    );
  }

  if (!invoice) {
    return (
      <EmptyState
        title="Invoice not found"
        description="The invoice you're looking for doesn't exist or you don't have access."
        action={
          <Button component={Link} to="/invoices">
            Back to Invoices
          </Button>
        }
      />
    );
  }

  const balanceDue = parseFloat(invoice.balance_due);
  const totalAmount = parseFloat(invoice.amount);
  const paidAmount = totalAmount - balanceDue;

  return (
    <Stack gap="lg">
      <Group>
        <Button
          component={Link}
          to="/invoices"
          variant="subtle"
          size="sm"
          leftSection={<IconArrowLeft size={16} />}
        >
          Invoices
        </Button>
      </Group>

      {discount?.eligible && (
        <Alert
          icon={<IconDiscount2 size={20} />}
          title="Early Payment Discount Available"
          color="green"
          variant="light"
        >
          Pay by {dayjs(discount.deadline).format('MMM D, YYYY')} to save ${discount.discount_amount}. You would pay ${discount.net_amount} instead of ${invoice.balance_due}.
        </Alert>
      )}

      <PageHeader
        title={invoice.invoice_number}
        description={`Order ${invoice.order_number ?? '—'}`}
        actions={
          <Group gap="sm">
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={handleDownloadPdf}
              loading={downloadPdf.isPending}
            >
              Download PDF
            </Button>
            <Button
              variant="light"
              leftSection={<IconMail size={16} />}
              onClick={handleEmailCopy}
              loading={emailCopy.isPending}
            >
              Email Me a Copy
            </Button>
            <Button
              variant="subtle"
              color="yellow"
              leftSection={<IconAlertTriangle size={16} />}
              onClick={openDispute}
            >
              Dispute
            </Button>
            {invoice.status !== 'paid' && balanceDue > 0 && (
              <>
                <Button
                  variant="light"
                  color="grape"
                  leftSection={<IconCalendar size={16} />}
                  onClick={openPlan}
                >
                  Set Up Payment Plan
                </Button>
                <Button
                  component={Link}
                  to={`/billing/pay?invoice=${invoice.id}`}
                  leftSection={<IconCreditCard size={16} />}
                >
                  Make Payment
                </Button>
              </>
            )}
          </Group>
        }
      />

      {/* Summary info */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <Paper p="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Status</Text>
          <Group mt={4}>
            <StatusBadge status={invoice.status} />
          </Group>
        </Paper>
        <Paper p="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Issued Date</Text>
          <Text fw={500} mt={4}>{dayjs(invoice.issued_date).format('MMM D, YYYY')}</Text>
        </Paper>
        <Paper p="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Due Date</Text>
          <Text fw={500} mt={4}>{dayjs(invoice.due_date).format('MMM D, YYYY')}</Text>
        </Paper>
        <Paper p="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>Balance Due</Text>
          <Text fw={700} size="lg" mt={4} c={balanceDue > 0 ? 'red' : 'brand'}>
            ${balanceDue.toFixed(2)}
          </Text>
        </Paper>
      </SimpleGrid>

      {/* Line items */}
      <Paper>
        <Text fw={600} mb="sm">Line Items</Text>
        {invoice.lines && invoice.lines.length > 0 ? (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Description</Table.Th>
                <Table.Th ta="right">Qty</Table.Th>
                <Table.Th ta="right">Unit Price</Table.Th>
                <Table.Th ta="right">Total</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invoice.lines.map((line) => (
                <Table.Tr key={line.id}>
                  <Table.Td><Text size="sm">{line.description}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">{line.quantity}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm">${parseFloat(line.unit_price).toFixed(2)}</Text></Table.Td>
                  <Table.Td ta="right"><Text size="sm" fw={500}>${parseFloat(line.total).toFixed(2)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
            <Table.Tfoot>
              <Table.Tr>
                <Table.Td colSpan={3} ta="right">
                  <Text fw={600} size="sm">Total</Text>
                </Table.Td>
                <Table.Td ta="right">
                  <Text fw={700} size="sm">${totalAmount.toFixed(2)}</Text>
                </Table.Td>
              </Table.Tr>
            </Table.Tfoot>
          </Table>
        ) : (
          <Text size="sm" c="dimmed">No line items available.</Text>
        )}
      </Paper>

      {/* Payment history */}
      <Paper>
        <Text fw={600} mb="sm">Payment History</Text>
        {invoice.payments && invoice.payments.length > 0 ? (
          <>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Payment #</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Method</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {invoice.payments.map((pmt) => (
                  <Table.Tr key={pmt.id}>
                    <Table.Td><Text size="sm" fw={500}>{pmt.payment_number}</Text></Table.Td>
                    <Table.Td><Text size="sm">{dayjs(pmt.paid_at).format('MMM D, YYYY')}</Text></Table.Td>
                    <Table.Td><Text size="sm" tt="capitalize">{pmt.method.replace(/_/g, ' ')}</Text></Table.Td>
                    <Table.Td><StatusBadge status={pmt.status} /></Table.Td>
                    <Table.Td ta="right"><Text size="sm" fw={500}>${parseFloat(pmt.amount).toFixed(2)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Divider my="sm" />
            <Group justify="flex-end" gap="xl">
              <Group gap="xs">
                <Text size="sm" c="dimmed">Total Paid:</Text>
                <Text size="sm" fw={600} c="brand">${paidAmount.toFixed(2)}</Text>
              </Group>
              <Group gap="xs">
                <Text size="sm" c="dimmed">Remaining:</Text>
                <Text size="sm" fw={600} c={balanceDue > 0 ? 'red' : 'brand'}>
                  ${balanceDue.toFixed(2)}
                </Text>
              </Group>
            </Group>
          </>
        ) : (
          <Text size="sm" c="dimmed">No payments recorded yet.</Text>
        )}
      </Paper>

      {invoice.notes && (
        <Paper>
          <Text fw={600} mb="xs">Notes</Text>
          <Text size="sm" c="dimmed">{invoice.notes}</Text>
        </Paper>
      )}

      {/* Dispute modal */}
      <Modal opened={disputeOpen} onClose={closeDispute} title="Dispute Invoice" size="md">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Submit a dispute for invoice {invoice.invoice_number}. Our team will review and respond within 2 business days.
          </Text>
          <Select
            label="Reason"
            placeholder="Select a reason"
            data={DISPUTE_REASONS}
            value={disputeReason}
            onChange={(v) => setDisputeReason(v ?? '')}
            required
          />
          <Textarea
            label="Details"
            placeholder="Describe the issue in detail..."
            value={disputeDetails}
            onChange={(e) => setDisputeDetails(e.currentTarget.value)}
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closeDispute}>Cancel</Button>
            <Button
              onClick={handleSubmitDispute}
              loading={submitDispute.isPending}
              disabled={!disputeReason}
            >
              Submit Dispute
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Payment Plan modal */}
      <Modal opened={planOpen} onClose={closePlan} title="Set Up Payment Plan" size="md">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Split your balance of ${balanceDue.toFixed(2)} into installments.
          </Text>
          <NumberInput
            label="Number of Installments"
            value={planInstallments}
            onChange={(v) => setPlanInstallments(typeof v === 'number' ? v : 3)}
            min={2}
            max={24}
          />
          <Select
            label="Frequency"
            data={[
              { value: 'weekly', label: 'Weekly' },
              { value: 'biweekly', label: 'Bi-weekly' },
              { value: 'monthly', label: 'Monthly' },
            ]}
            value={planFrequency}
            onChange={(v) => setPlanFrequency(v ?? 'monthly')}
          />
          <Paper p="sm" withBorder>
            <Text size="sm" fw={500}>
              {planInstallments} payments of ~${(balanceDue / planInstallments).toFixed(2)} ({planFrequency})
            </Text>
          </Paper>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={closePlan}>Cancel</Button>
            <Button onClick={handleCreatePlan} loading={createPlan.isPending}>
              Create Plan
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
